<script lang="ts">
	import { getTranslate } from '@tolgee/svelte';
	import SignUpProgress from './SignUpProgress.svelte';
	import { createEventDispatcher, onMount } from 'svelte';
	import { classes } from '$lib/utils';
	import SignUpStep0 from './Steps/SignUpStep0.svelte';
	import SignUpSteps123 from './Steps/SignUpSteps123.svelte';
	import SignUpSteps2Other from './Steps/SignUpSteps2Other.svelte';
	import { page } from '$app/stores';
	import type { SignUpStepData } from '$lib/types/form';
	import LeftArrowIcon from '$lib/components/Icon/LeftArrowIcon.svelte';
	import SignUpStepMethodChoice from './Steps/SignUpStepMethodChoice.svelte';
	import { LOGIN_MISSING_META, SIGN_UP_STEP } from '$lib/constants';
	import type { User } from '@supabase/supabase-js';

	export let additionalClass = '';

	const { t } = getTranslate();
	const dispatch = createEventDispatcher();

	let maxStep: number = 3;
	let currentStep: number = -1;
	let minMetaStep: number = 0;
	let socialStep: boolean = false;
	let isOther: boolean = false;
	let missingMetaLogin: boolean = false;

	const steps123Items: SignUpStepData[] = [
		{
			step: 1,
			field: 'known_from',
			options: [
				{ id: 'linkedIn', title: 'LinkedIn' },
				{ id: 'friend', title: 'From a Friend' },
				{ id: 'google', title: 'Google' },
				{ id: 'other', title: 'Other' }
			]
		},
		{
			step: 2,
			field: 'experience_level',
			options: [
				{ id: 'beginner', title: 'Beginner' },
				{ id: 'intermediate', title: 'Intermediate' },
				{ id: 'advanced', title: 'Advanced' }
			]
		},
		{
			step: 3,
			field: 'job_title',
			options: [
				{ id: 'dataAnalyst', title: 'Data Analyst' },
				{ id: 'dataAnalyst', title: 'Data Engineer' },
				{ id: 'dataAnalyst', title: 'Analytics Engineer' },
				{ id: 'dataAnalyst', title: 'Data Scientist' },
				{ id: 'product', title: 'Product' },
				{ id: 'growth', title: 'Growth' },
				{ id: 'management', title: 'Management' },
				{ id: 'student', title: 'Student' }
			],
			additionalClass: 'grid-cols-2'
		}
	];

	onMount(() => {
		initCurrentStep();
	});

	function initCurrentStep() {
		socialStep = $page.url.searchParams.get('signup') === 'social';
		missingMetaLogin = !!localStorage.getItem(LOGIN_MISSING_META);
		currentStep = -1;
		const user: User | undefined = $page.data.session?.user;
		const signUpStep: string | null = localStorage.getItem(SIGN_UP_STEP);

		if (user && (!socialStep || (signUpStep && parseInt(signUpStep) !== 0))) {
			if (signUpStep) {
				currentStep = parseInt(signUpStep);
				minMetaStep = currentStep;
			} else {
				currentStep = 1;
			}
		} else if (user) {
			currentStep = 0;
		}
		// } // TODO: check for profile
	}

	function handleStepSuccess(socialSignUp?: boolean) {
		if (socialSignUp) {
			socialStep = true;
			maxStep = 2;
		} else if (isOther) {
			isOther = false;
			currentStep = currentStep + 1;
		} else {
			currentStep = currentStep + 1;
		}

		if (currentStep > maxStep) {
			dispatch('success');
		}
	}

	function handleStepError(error: Error) {
		alert(error);
	}
</script>

<div class={classes(additionalClass, 'flex flex-col items-center justify-center')}>
	<div class="flex flex-col items-center w-full">
		{#if currentStep === -1}
			<SignUpStepMethodChoice
				on:emailSuccess={() => handleStepSuccess()}
				on:success={() => handleStepSuccess(true)}
			/>
		{:else if currentStep <= maxStep}
			<div class="mb-16">
				{#if currentStep === 0}
					{#if !missingMetaLogin && !socialStep}
						<button class="mb-8 place-self-start" on:click={() => (currentStep -= 1)}
							><LeftArrowIcon /></button
						>
					{/if}
					<div class="flex flex-col items-center">
						<SignUpStep0 {socialStep} on:success={() => handleStepSuccess()} />
					</div>
				{:else if currentStep <= maxStep && !isOther}
					{#each steps123Items.filter((s) => s.step === currentStep) as item}
						<div class="flex flex-col items-center">
							{#if !missingMetaLogin || (missingMetaLogin && currentStep > minMetaStep)}
								<button class="mb-8 place-self-start" on:click={() => (currentStep -= 1)}
									><LeftArrowIcon /></button
								>
							{/if}
							<SignUpSteps123
								step={item.step}
								field={item.field}
								options={item.options}
								on:error={({ detail }) => handleStepError(detail.error)}
								on:success={() => handleStepSuccess()}
								on:other={() => (isOther = true)}
								additionalClass={item.additionalClass || ''}
							/>
						</div>
					{/each}
				{:else if isOther}
					<div class="flex flex-col items-center">
						<button class="mb-8 place-self-start" on:click={() => (isOther = false)}
							><LeftArrowIcon /></button
						>
						<SignUpSteps2Other
							step={currentStep}
							field="known_from"
							option={{ id: 'other', title: 'Other' }}
							on:error={({ detail }) => handleStepError(detail.error)}
							on:success={() => handleStepSuccess()}
							additionalClass="grid-cols-1"
						/>
					</div>
				{/if}
			</div>
			<SignUpProgress steps={maxStep + 1 - minMetaStep} currentStep={currentStep - minMetaStep} />
		{:else}
			<div class="flex justify-center p-4 my-16 text-xl font-bold">
				{@html $t('signup.step-finish.headline')}
			</div>
		{/if}
	</div>
</div>
